import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import Script from 'next/script';
import getConfig from 'next/config';
import each from 'lodash/each';
import ErrorMessage from 'components/ui/ErrorMessage/ErrorMessage';
import { valueEmpty } from '../../helpers';

let autocomplete;

const NO_AUTOCOMPLETE_ATTRIBUTE = 'boataddressfieldnoautocomplete';

const { GOOGLE_API } = getConfig().publicRuntimeConfig;

const isDefined = (value) => value !== undefined;

const boatLocationPayload = (locationName, place = {}, latlng = {}) => {
  const addressComponents = place.address_components;
  const components = {};

  each(addressComponents, (component) => {
    const addressType = component.types[0];
    components.country_code = component[''];
    each(addressType, () => {
      if (!component) {
        return;
      }

      if (addressType === 'country') {
        components[addressType] = { long_name: component.long_name, short_name: component.short_name };
      } else {
        components[addressType] = component.long_name;
      }
    });
  });

  const {
    street_number,
    route,
    neighborhood,
    locality,
    sublocality_level_1,
    sublocality_level_2,
    administrative_area_level_2,
    administrative_area_level_1,
    country,
    postal_code,
  } = components;

  const { lat, lng } = latlng;

  const location = {
    street_1: `${street_number || ''} ${route || ''}`,
    street_2: '',
    city: locality || sublocality_level_1 || sublocality_level_2 || neighborhood,
    state: administrative_area_level_1,
    postal_code: postal_code || '00000',
    country_code: (country && country.short_name) || '',
    country_name: (country && country.long_name) || '',
    county_name: administrative_area_level_2,
    latitude: lat,
    longitude: lng,
  };

  return location;
};

const LocationInput = (props) => {
  const {
    name,
    placeholder,
    defaultValue,
    showError,
    onChange,
    withPin,
    errorMessage,
    locationForm,
    onBlur,
    onFocus,
    noSecondError = true,
  } = props;
  const { register, setValue, errors, watch, trigger, formState } = useFormContext();

  const [isFocused, setFocused] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [hasRelevantErrors, setHasRelevantErrors] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      if (scriptLoaded || window.mapsLoaded) {
        handleScriptLoad();
      }
    }, 500);
  }, [scriptLoaded]);

  useEffect(() => {
    const countryCode = watch('country_code');
    if (countryCode && locationForm && autocomplete) {
      const setCountryAutocomplete = countryCode.toLowerCase();
      autocomplete.setComponentRestrictions({ country: setCountryAutocomplete });
    }
  }, [locationForm, watch]);

  const forceValidation = useCallback(async () => {
    // trigger forces react-hook-form to run validation, giving feedback at
    // the time that this callback runs.
    await trigger(['address', 'street_1', 'city', 'state', 'postal_code', 'country_code']);
    setHasRelevantErrors(isDefined(errors.address) || isDefined(errors.street_1));
  }, [errors, trigger]);

  const handlePlaceSelect = useCallback(async () => {
    const place = autocomplete.getPlace();

    const locationName = watch(name);

    if (place) {
      // Important change: Set noPlaceSelected to false directly
      if (onChange) onChange(false);
      let formattedAddress = place.formatted_address;

      if (place.geometry) {
        const coords = place.geometry.location;
        formattedAddress = place.formatted_address;
        // remove the country
        if (formattedAddress.split(',').length > 1) {
          formattedAddress = formattedAddress.slice(0, formattedAddress.lastIndexOf(','));
        }

        const latlng = { lat: coords.lat(), lng: coords.lng() };

        setValue('lat', coords.lat());
        setValue('lng', coords.lng());
        setValue('owner_pin_latitude', coords.lat());
        setValue('owner_pin_longitude', coords.lng());
        setValue('near', formattedAddress);

        const values = boatLocationPayload(locationName, place, latlng);

        Object.entries(values).forEach(async (c) => {
          setValue(c[0], c[1]);
        });
      }
    }

    // trigger forces react-hook-form to run validation, giving feedback at
    // the time that this callback runs.
    await forceValidation();
  }, [name, onChange, setValue, watch, forceValidation]);

  const handleScriptLoad = useCallback(() => {
    const options = {
      types: ['geocode', 'establishment'],
    };

    const inputEl = document.getElementById(name);

    if (window.google) {
      autocomplete = new window.google.maps.places.Autocomplete(inputEl, options);

      autocomplete.setFields(['address_components', 'geometry', 'formatted_address']);
      autocomplete.addListener('place_changed', handlePlaceSelect);
      if (inputEl) {
        inputEl.setAttribute('autocomplete', NO_AUTOCOMPLETE_ATTRIBUTE);
      }
    }
  }, [handlePlaceSelect, name]);

  return (
    <div className="u-posRelative">
      <Script
        strategy="afterInteractive"
        onLoad={() => {
          setScriptLoaded(true);
        }}
        src={`https://maps.googleapis.com/maps/api/js?libraries=places,geometry&key=${GOOGLE_API}&callback=initMaps`}
      />

      <input name="lat" type="hidden" ref={register} />
      <input name="lng" type="hidden" ref={register} />
      <label
        className={`InputText-label ${withPin ? 'InputText-label--withIcon' : ''} ${
          !hasRelevantErrors || !valueEmpty(watch(name)) || isFocused ? 'InputText-label--isFocused' : ''
        }`}
      >
        {placeholder}
      </label>
      <input
        data-testid="component-locationInput-address-input"
        id={name}
        className={`InputText InputText--withAnimatedLabel ${
          withPin ? 'InputText--withIconBig' : ''
        } u-fsSm  u-sizeFull  ${showError && hasRelevantErrors ? ' is-invalid' : ''}`}
        name={name}
        defaultValue={defaultValue}
        type="text"
        ref={register({
          validate: (value) => !valueEmpty(value) && noSecondError,
        })}
        autoComplete="off"
        onChange={() => {
          if (onChange) onChange(true);
          // if (formState.dirtyFields.address) forceValidation();
        }}
        placeholder=""
        onBlur={async () => {
          setFocused(false);
          if (onBlur) {
            onBlur();
          }
          await forceValidation();
        }}
        onFocus={async () => {
          // The on focus event can trigger when the field is invalid and
          // react-hook-form automatically focuses it. That's our cue to run the
          // focus handler to check if there are any errors and trigger error
          // highlighting and text.
          if (formState.dirtyFields.address) await forceValidation();

          setFocused(true);
          if (onFocus) {
            onFocus();
          }
          // Nasty hack but it works
          document.getElementById(name).setAttribute('autocomplete', 'off');
        }}
      />
      {withPin ? (
        <svg
          height={25}
          fill="#8e9697"
          style={{ top: '25%', left: '12px', position: 'absolute' }}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1008 1008"
        >
          <path d="M504 28.1c-195.1 0-352.1 157-352.1 352.1 0 180.8 290.3 542.5 323.6 585.3 4.8 9.5 19 14.3 28.6 14.3 9.5 0 19-4.8 28.6-14.3C566 922.7 856.3 561 856.3 380.2 851.4 185.2 699.1 28.1 504 28.1zM223.2 380.3c0-152.3 123.7-280.8 276-280.8S780 223.2 780 375.5c0 119-180.8 375.9-276 504.4-95.2-123.7-280.8-380.7-280.8-499.6zm347.4 0c0 38.1-28.6 66.6-66.6 66.6s-66.6-28.6-66.6-66.6 28.6-66.6 66.6-66.6 66.6 28.5 66.6 66.6z" />
        </svg>
      ) : null}

      {showError && (
        <ErrorMessage className="u-posAbsolute ControlError-list" error={hasRelevantErrors} message={errorMessage} />
      )}
    </div>
  );
};

LocationInput.defaultProps = {
  label: '',
  name: 'address',
  placeholder: 'Address',
  defaultValue: '',
  action: () => null,
  showError: true,
  isValid: true,
  onChange: () => null,
  onBlur: () => null,
  onFocus: () => null,
  withPin: true,
  locationForm: false,
  errorMessage: 'This field is required',
  noSecondError: true,
};

export default LocationInput;
